import type {
  AdvisorAuditStatus,
  AnalysisStatus,
  AppointmentStatus,
  AuditFindingSeverity,
  CallStatus,
  CampaignState,
  ConnectionStatus,
  ContactImportStatus,
  ConversationMode,
  DeliveryStatus,
  DeploymentStatus,
  DocumentStatus,
  EnrollmentStatus,
  OpportunityControlState,
  ImportRowOutcome,
  PermissionStatus,
  ProjectStatus,
  ReceiptStatus,
  RecipientStatus,
  RoutineState,
  SetupStepStatus,
  StepStatus,
  UnitAvailability,
  UsageBudgetState,
  VersionState,
  WebhookDeliveryStatus,
} from '@lia/contracts';
import type { StatusTone } from '../primitives/badge.js';

// Semantic tone for each domain status. Labels come from '@lia/contracts' (*_LABELS).

export const CONNECTION_STATUS_TONE: Record<ConnectionStatus, StatusTone> = {
  pending: 'info',
  connected: 'success',
  needs_attention: 'warning',
  disconnected: 'neutral',
  revoked: 'danger',
};

export const SETUP_STATUS_TONE: Record<SetupStepStatus, StatusTone> = {
  pending: 'neutral',
  in_progress: 'info',
  ready: 'success',
  skipped: 'neutral',
  needs_attention: 'warning',
};

export const CONVERSATION_MODE_TONE: Record<ConversationMode, StatusTone> = {
  ai_active: 'accent',
  human_owned: 'info',
  paused: 'warning',
  closed: 'neutral',
};

/** Handoff state of an opportunity (spec §05.3, §05.23): pending = someone must act; final = a person owns it. */
export const OPPORTUNITY_CONTROL_TONE: Record<OpportunityControlState, StatusTone> = {
  ai_active: 'accent',
  handoff_pending: 'warning',
  human_final: 'info',
  human_temp: 'warning',
};

export const DELIVERY_STATUS_TONE: Record<DeliveryStatus, StatusTone> = {
  queued: 'neutral',
  provider_accepted: 'neutral',
  delivered: 'success',
  read: 'success',
  failed: 'danger',
  unknown: 'warning',
};

export const CALL_STATUS_TONE: Record<CallStatus, StatusTone> = {
  queued: 'neutral',
  provider_accepted: 'neutral',
  ringing: 'info',
  answered: 'info',
  completed: 'success',
  no_answer: 'warning',
  busy: 'warning',
  voicemail: 'warning',
  failed: 'danger',
  uncertain: 'warning',
};

export const ANALYSIS_STATUS_LABELS: Record<AnalysisStatus, string> = {
  not_available: 'Sin análisis',
  processing: 'Analizando',
  ready: 'Análisis listo',
  partial: 'Análisis parcial',
  failed: 'Análisis falló',
};

export const ANALYSIS_STATUS_TONE: Record<AnalysisStatus, StatusTone> = {
  not_available: 'neutral',
  processing: 'info',
  ready: 'success',
  partial: 'warning',
  failed: 'danger',
};

export const APPOINTMENT_STATUS_TONE: Record<AppointmentStatus, StatusTone> = {
  proposed: 'neutral',
  booking: 'info',
  confirmed: 'success',
  reschedule_requested: 'warning',
  cancel_requested: 'warning',
  cancelled: 'neutral',
  completed: 'success',
  no_show: 'danger',
  failed: 'danger',
  reconciliation_required: 'warning',
};

export const UNIT_AVAILABILITY_TONE: Record<UnitAvailability, StatusTone> = {
  available: 'success',
  held: 'warning',
  reserved: 'info',
  sold: 'neutral',
  unavailable: 'neutral',
  unknown: 'warning',
};

export const VERSION_STATE_TONE: Record<VersionState, StatusTone> = {
  draft: 'neutral',
  tested: 'info',
  published: 'success',
  archived: 'neutral',
};

export const DEPLOYMENT_STATUS_TONE: Record<DeploymentStatus, StatusTone> = {
  pending: 'info',
  applied: 'success',
  failed: 'danger',
  action_required: 'warning',
};

export const WEBHOOK_DELIVERY_STATUS_TONE: Record<
  WebhookDeliveryStatus,
  StatusTone
> = {
  pending: 'neutral',
  delivering: 'info',
  succeeded: 'success',
  failed: 'warning',
  dead_letter: 'danger',
};

export const ROUTINE_STATE_TONE: Record<RoutineState, StatusTone> = {
  draft: 'neutral',
  test: 'info',
  live: 'success',
  paused: 'warning',
};

export const PROJECT_STATUS_TONE: Record<ProjectStatus, StatusTone> = {
  draft: 'neutral',
  active: 'success',
  paused: 'warning',
  archived: 'neutral',
};

export const DOCUMENT_STATUS_TONE: Record<DocumentStatus, StatusTone> = {
  uploaded: 'info',
  processing: 'info',
  needs_review: 'warning',
  published: 'success',
  failed: 'danger',
  superseded: 'neutral',
};

export const RECEIPT_STATUS_TONE: Record<ReceiptStatus, StatusTone> = {
  accepted: 'neutral',
  processing: 'info',
  applied: 'success',
  skipped: 'neutral',
  failed: 'danger',
  quarantined: 'warning',
};

export const CAMPAIGN_STATE_TONE: Record<CampaignState, StatusTone> = {
  draft: 'neutral',
  pending_approval: 'warning',
  approved: 'info',
  scheduled: 'info',
  running: 'accent',
  paused: 'warning',
  completed: 'success',
  cancelled: 'neutral',
};

export const RECIPIENT_STATUS_TONE: Record<RecipientStatus, StatusTone> = {
  pending: 'neutral',
  excluded: 'neutral',
  queued: 'info',
  sent: 'info',
  delivered: 'success',
  read: 'success',
  replied: 'accent',
  failed: 'danger',
  skipped: 'warning',
};

export const CONTACT_IMPORT_STATUS_TONE: Record<
  ContactImportStatus,
  StatusTone
> = {
  uploaded: 'neutral',
  previewed: 'info',
  applying: 'accent',
  applied: 'success',
  failed: 'danger',
  cancelled: 'neutral',
};

export const IMPORT_ROW_OUTCOME_TONE: Record<ImportRowOutcome, StatusTone> = {
  create: 'success',
  update: 'info',
  invalid: 'danger',
  duplicate: 'warning',
  ambiguous: 'warning',
};

/** Contact consent per channel. */
export const PERMISSION_STATUS_TONE: Record<PermissionStatus, StatusTone> = {
  granted: 'success',
  denied: 'danger',
  revoked: 'danger',
  unknown: 'warning',
};

export const ENROLLMENT_STATUS_TONE: Record<EnrollmentStatus, StatusTone> = {
  active: 'accent',
  paused: 'warning',
  completed: 'success',
  exited: 'neutral',
};

export const STEP_STATUS_TONE: Record<StepStatus, StatusTone> = {
  scheduled: 'info',
  running: 'accent',
  done: 'success',
  skipped: 'warning',
  failed: 'danger',
  suspended: 'warning',
};

export const USAGE_BUDGET_STATE_TONE: Record<UsageBudgetState, StatusTone> = {
  ok: 'success',
  warning: 'warning',
  exceeded: 'danger',
};

export const ADVISOR_AUDIT_STATUS_TONE: Record<AdvisorAuditStatus, StatusTone> =
  {
    pending: 'info',
    completed: 'success',
    insufficient_evidence: 'warning',
    failed: 'danger',
    reviewed: 'accent',
  };

export const AUDIT_FINDING_SEVERITY_TONE: Record<
  AuditFindingSeverity,
  StatusTone
> = { positive: 'success', minor: 'warning', major: 'danger' };

/** Provider capability checks (Calendly, Aircall, WhatsApp): verified / unverified / unsupported / failed / skipped. */
export type CapabilityCheckState =
  | 'verified'
  | 'unverified'
  | 'unsupported'
  | 'failed'
  | 'skipped';

export const CAPABILITY_CHECK_TONE: Record<CapabilityCheckState, StatusTone> = {
  verified: 'success',
  unverified: 'neutral',
  unsupported: 'warning',
  failed: 'danger',
  skipped: 'neutral',
};
